/**
 * @file wallboardRecovery.js
 * @description Self-healing helpers for the unattended TV wallboard.
 *
 * The wall has nobody at the keyboard. Anything that leaves it blank — a
 * lazy chunk that 404s because a reload landed mid-deploy, a render crash,
 * an access probe that timed out on a Wi-Fi blip — has to recover on its own
 * or it stays broken until someone walks over and presses F5.
 *
 * Two pieces live here so they are unit-testable without React:
 *   - crash-reload backoff: how long to wait before the boundary reloads,
 *     growing with each consecutive failed attempt so a genuinely broken
 *     build cannot thrash the display (or hosting) in a tight loop.
 *   - build-change detection: whether hosting's /version.json reports a
 *     different build to the one running, so the wall only reloads when a
 *     reload would actually change something.
 */

import { FRONTEND_BUILD_ID, FRONTEND_BUILD_VERSION, normalizeAppVersion } from '../utils/appVersion';

const CRASH_ATTEMPT_KEY = 'wallboardCrashReload';

/** Reload delays per consecutive crash: 15s, 30s, 1m, 2m, then 5m forever. */
export const CRASH_RELOAD_DELAYS_MS = [15000, 30000, 60000, 120000, 300000];

/** A crash this long after the previous one counts as a fresh run, not a loop. */
export const CRASH_STREAK_RESET_MS = 10 * 60 * 1000;

function readAttempt(storage) {
    try {
        const raw = storage.getItem(CRASH_ATTEMPT_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed.count !== 'number') return null;
        return parsed;
    } catch (_) {
        return null;
    }
}

/**
 * Record one more crash and return how long to wait before reloading.
 * Streaks reset once the previous crash is old enough that the page must have
 * been running fine in between.
 */
export function recordCrashAndGetDelay({ storage = window.sessionStorage, now = Date.now() } = {}) {
    const prev = readAttempt(storage);
    const streak = prev && now - (prev.at || 0) < CRASH_STREAK_RESET_MS ? prev.count + 1 : 1;
    try {
        storage.setItem(CRASH_ATTEMPT_KEY, JSON.stringify({ count: streak, at: now }));
    } catch (_) {
        // Private mode / quota: still back off, just without memory across reloads.
    }
    const idx = Math.min(streak, CRASH_RELOAD_DELAYS_MS.length) - 1;
    return { delayMs: CRASH_RELOAD_DELAYS_MS[idx], attempt: streak };
}

/** Called once the board has mounted and rendered: the streak is over. */
export function clearCrashStreak({ storage = window.sessionStorage } = {}) {
    try {
        storage.removeItem(CRASH_ATTEMPT_KEY);
    } catch (_) {
        // ignore
    }
}

/**
 * Fetch the build hosting would serve on reload. Mirrors the staff app's
 * update monitor: version.json is no-store and bypasses the service worker,
 * and old deploys rewrote it to index.html (hence the content-type check).
 * Returns null when it cannot be read — callers treat that as "unknown", not
 * as "changed".
 */
export async function fetchHostedBuild({ fetchImpl = fetch, timeoutMs = 10000 } = {}) {
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
    try {
        const res = await fetchImpl(`/version.json?_=${Date.now()}`, {
            cache: 'no-store',
            signal: controller ? controller.signal : undefined
        });
        if (!res.ok) return null;
        const contentType = res.headers.get('content-type') || '';
        if (!contentType.includes('json')) return null;
        const data = await res.json();
        const version = normalizeAppVersion(data?.version);
        if (!version) return null;
        return { version, buildId: String(data?.buildId || '').trim() };
    } catch (_) {
        return null;
    } finally {
        if (timer) clearTimeout(timer);
    }
}

/**
 * True only when hosting reports a build that differs from the one running.
 * buildId (commit SHA) wins when both sides have one; version is the fallback
 * for builds from before SHA stamping. Unknown (null) is never "different".
 */
export function isDifferentBuild(hosted, running = { version: FRONTEND_BUILD_VERSION, buildId: FRONTEND_BUILD_ID }) {
    if (!hosted) return false;
    if (hosted.buildId && running.buildId) return hosted.buildId !== running.buildId;
    const left = normalizeAppVersion(hosted.version);
    const right = normalizeAppVersion(running.version);
    if (!left || !right) return false;
    return left !== right;
}
